import { FC, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Menu, MenuItem } from '@mui/material';
import { Dataset, DatasetAccessStatus, DatasetStatus } from '~/api/types';
import { useSendDatasetToAtlanMutation } from '~/api/v2/hooks';
import { Tooltip } from '~/components';
import { tableActionButtonStyles } from '~/components/styles';
import { VerticalDotsIcon } from '~/icons';
import { DatasetDetailsTabsEnum } from '~/pages/DatasetDetailsPage/datasetDetailsTabs';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUser } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { getPriviligies } from '~/utils';

interface ActionsCellProps {
  datasetInfo: Dataset;
}

const ActionsCell: FC<ActionsCellProps> = ({ datasetInfo }) => {
  const { id, status, access_status, status_edit_allowed, details_edit_allowed, publish_to_atlan_allowed } =
    datasetInfo;
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const [sendToAtlan, { isLoading }] = useSendDatasetToAtlanMutation();
  const isDatasetGuaranteed = access_status !== DatasetAccessStatus.NO_ACCESS;
  const userPriviligies = getPriviligies(user?.role?.id);
  const isManageAccessAllowed = userPriviligies?.manageDatasets.canView || status_edit_allowed || false;
  const canRequestAccess = status === DatasetStatus.LOCKED_SEARCHABLE;

  return (
    <>
      <Button
        disabled={!isManageAccessAllowed && isDatasetGuaranteed}
        sx={tableActionButtonStyles}
        endIcon={<VerticalDotsIcon style={{ marginLeft: '1rem' }} />}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        More
      </Button>
      <Menu
        elevation={1}
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        {(details_edit_allowed || status_edit_allowed) && (
          <MenuItem
            onClick={() => {
              navigate(`${id}`, { relative: 'path' });
              dispatch(openModal({ type: ModalTypesEnum.EDIT_DATASET, payload: { datasetId: id } }));
            }}
          >
            Edit dataset
          </MenuItem>
        )}
        {isManageAccessAllowed && (
          <MenuItem
            onClick={() => {
              navigate(`${id}`, {
                relative: 'path',
                state: { from: 'data-catalog', tab: DatasetDetailsTabsEnum.MANAGE_ACCESS },
              });
              setAnchorEl(null);
            }}
          >
            Manage dataset access
          </MenuItem>
        )}
        {!isDatasetGuaranteed && (
          <Tooltip title={!canRequestAccess ? 'Request Access is enabled only for Locked-Searchable datasets' : ''}>
            <span>
              <MenuItem
                disabled={!canRequestAccess}
                onClick={() => {
                  dispatch(openModal({ type: ModalTypesEnum.REQUEST_DATASET_ACCESS, payload: { datasetId: id } }));
                  setAnchorEl(null);
                }}
              >
                Request Access
              </MenuItem>
            </span>
          </Tooltip>
        )}
        <MenuItem
          disabled={isLoading || !publish_to_atlan_allowed}
          onClick={() => {
            dispatch(
              openModal({
                type: ModalTypesEnum.PUBLISH_TO_ATLAN,
                payload: { onSubmit: () => sendToAtlan({ datasetId: id }) },
              }),
            );
            setAnchorEl(null);
          }}
        >
          Publish dataset to Atlan
        </MenuItem>
      </Menu>
    </>
  );
};

export default ActionsCell;
